import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { reportFormError } from '@/lib/errorReporting';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import type { Edificio } from '@/services/buildingService';
import { toast } from 'sonner';

const formSchema = z.object({
  nombre_edificio: z.string().min(1, { message: "El nombre del edificio es requerido." }),
  direccion: z.string().optional(),
  numero_unidades: z.number().min(1, { message: "Debe tener al menos 1 unidad." }),
});

type FormValues = z.infer<typeof formSchema>;

interface BuildingFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (building: Edificio) => void;
  initialData?: Edificio | null;
}

export default function BuildingForm({ isOpen, onClose, onSave, initialData }: BuildingFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nombre_edificio: initialData?.nombre_edificio || "",
      direccion: initialData?.direccion || "",
      numero_unidades: initialData?.numero_unidades || 1,
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        nombre_edificio: initialData.nombre_edificio,
        direccion: initialData.direccion || "",
        numero_unidades: initialData.numero_unidades,
      });
    } else {
      form.reset({
        nombre_edificio: "",
        direccion: "",
        numero_unidades: 1,
      });
    }
  }, [initialData, form]);

  async function onSubmit(data: FormValues) {
    try {
      onSave({
        id_edificio: initialData?.id_edificio || "",
        nombre_edificio: data.nombre_edificio,
        direccion: data.direccion || "",
        numero_unidades: data.numero_unidades,
      });
      
      onClose();
      toast.success(initialData ? "Edificio actualizado exitosamente." : "Edificio creado exitosamente.");
    } catch (error) {
      reportFormError(error, data, {
        component: 'BuildingForm',
        action: initialData ? 'update' : 'create',
        recordId: initialData?.id_edificio,
        additionalContext: {
          isUpdate: !!initialData
        }
      });
      toast.error("Error al guardar el edificio");
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{initialData ? "Editar Edificio" : "Nuevo Edificio"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nombre_edificio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre del Edificio</FormLabel>
                  <FormControl>
                    <Input placeholder="Nombre del Edificio" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="direccion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dirección</FormLabel>
                  <FormControl>
                    <Input placeholder="Dirección" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="numero_unidades"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número de Unidades</FormLabel>
                  <FormControl>
                    <Input 
                      type="number"
                      min={1}
                      placeholder="Número de Unidades"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end space-x-4 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit">
                {initialData ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
